import React from "react";
import SortDropDown from "src/components/SortDropDown";
import LayoutWithSideBar from "src/layouts/LayoutWithSideBar";
import { AccessResource, ActionType } from "src/types/accessControl";

import ConfirmationModal from "src/components/ConfirmationModal";
import GroupSearchInput from "src/components/GroupSearchInput";
import POSCustomTabs from "src/components/POSCustomTabs";
import ScanQRModal from "src/components/ScanQRModal";
import Spinner from "src/components/Spinner";
import AddProductModal from "./AddProductModal";
import CartSection from "./CartSection";
import usePOSHandler, {
  POSCartItemType,
  SelectedPackageProductType,
} from "./hooks/usePOSHandler";
import { PosCartSchemaType } from "./posCartSchema";
import POSContainer from "./POSContainer";
import { TransactionSummarySchemaType } from "./TransactionSummary/transactionSummarySchema";
import SwitchButton from "src/components/SwitchButton";
import { CiGrid41 } from "react-icons/ci";
import DocumentIcon from "../../assets/images/document_icon.svg";

export interface POSProps {
  formValues?: PosCartSchemaType;
  posCartItems?: POSCartItemType[];
  selectedPackageProducts?: SelectedPackageProductType[];
  transactionSummaryFormValues?: TransactionSummarySchemaType;
  isLoadingDetailData?: boolean;
}

export const posViewOptions = [
  {
    value: "list",
    icon: (
      <img
        src={DocumentIcon}
        alt="document"
        style={{ height: 21, width: 21 }}
      />
    ),
  },
  {
    value: "grid",
    icon: <CiGrid41 style={{ height: 21, width: 21 }} />,
  },
];
export type PosViewOptionValue = (typeof posViewOptions)[number]["value"];

const POS: React.FC<POSProps> = (props) => {
  const {
    isAllowedBusinessOperation,
    activeSubCategoryTabValue,
    isEditOrder,
    transactionId,
    posItems,
    isLoading,
    isSubmitting,
    form,
    sort,
    setSort,
    view,
    setView,
    selectedPOSItem,
    selectedPosOrderItem,
    selectedPosOrderItemIndex,
    posCartItems,
    setPOSCartItems,
    selectedPackageProducts,
    setSelectedPackageProducts,
    isShowScanQRModal,
    isShowAddProductModal,
    isShowRepeatTransactionModal,
    handleClickSubCategory,
    promoCodeSortOptions,
    tabs,
    fetchNextPage,
    hasNextPage,
    handleAddProductToCart,
    handleCloseAddProductModal,
    handleEditOrderItem,
    handleShowRepeatTransactionModal,
    handleCloseRepeatTransactionModal,
    handleGoToPaymentSummary,
    handleSelectSearchedItem,
    handleScanQR,
    handleCloseScanQRModal,
    handleSuccessScanQR,
  } = usePOSHandler({ ...props });

  return (
    <div
    // className="flex gap-5 max-h-screen overflow-hidden"
    >
      <LayoutWithSideBar
        title=""
        resource={AccessResource["POS"]}
        action={ActionType["Allow"]}
        header={
          <div className="flex w-full justify-center">
            <div className="w-3/5">
              <GroupSearchInput
                selectedIds={[]}
                onValueChange={handleSelectSearchedItem}
                type="all"
              />
            </div>
          </div>
        }
        headerAction={
          <div className="flex w-full gap-4 -mb-6">
            <div className="flex-auto">
              <POSCustomTabs
                defaultTab={activeSubCategoryTabValue}
                tabs={tabs}
                onClick={handleClickSubCategory}
              />
            </div>

            <div className="flex flex-row gap-4 mb-6">
              <SortDropDown
                sort={sort}
                setSort={setSort}
                options={promoCodeSortOptions}
              />

              <SwitchButton
                value={view}
                setValue={setView}
                icons={posViewOptions}
              />
            </div>
          </div>
        }
        children2={
          <CartSection
            isEditOrder={isEditOrder}
            transactionId={transactionId}
            isLoading={props.isLoadingDetailData ?? false}
            isSubmitting={isSubmitting}
            form={form}
            posCartItems={posCartItems}
            selectedPackageProducts={selectedPackageProducts}
            handleEditOrderItem={handleEditOrderItem}
            handleShowRepeatTransactionModal={handleShowRepeatTransactionModal}
            handleGoToPaymentSummary={handleGoToPaymentSummary}
            handleScanQR={handleScanQR}
          />
        }
      >
        {!isLoading ? (
          <POSContainer
            view={view}
            data={posItems}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            handleAddProductToCart={handleAddProductToCart}
          />
        ) : (
          <div
            className="flex justify-center items-center"
            style={{ minHeight: "70vh" }}
          >
            <Spinner size="large" />
          </div>
        )}
      </LayoutWithSideBar>

      {isShowAddProductModal && selectedPOSItem && (
        <AddProductModal
          isEditOrder={isEditOrder}
          transactionId={transactionId}
          isShow={isShowAddProductModal}
          onClose={handleCloseAddProductModal}
          selectedPOSItem={selectedPOSItem}
          selectedPosOrderItem={selectedPosOrderItem}
          selectedPosOrderItemIndex={selectedPosOrderItemIndex}
          selectedPackageProducts={selectedPackageProducts}
          setSelectedPackageProducts={setSelectedPackageProducts}
          posForm={form}
          setPOSCartItems={setPOSCartItems}
        />
      )}

      <ConfirmationModal
        isShow={isShowRepeatTransactionModal}
        onClose={handleCloseRepeatTransactionModal}
        onSubmit={handleGoToPaymentSummary}
        isSubmitting={isSubmitting}
        title="Repeat Transaction?"
        description="You just made a transaction with the same customer. do you still want to continue?"
        cancelButtonTitle="Cancel Order"
        submitButtonTitle="Continue Order"
        cancelButtonPosition="first"
        footerAlignment="between"
      />

      <ScanQRModal
        isShow={isShowScanQRModal}
        onClose={handleCloseScanQRModal}
        onSuccess={handleSuccessScanQR}
      />
    </div>
  );
};

export default POS;
